import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CreditCard, ArrowRight, WarningCircle, SignOut, ArrowSquareOut } from "@phosphor-icons/react";
import { toast } from "sonner";
import BrandMark from "../components/BrandMark";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const STATUS_LABEL = {
  trialing: { label: "Free trial", color: "bg-emerald-100 text-emerald-800" },
  active: { label: "Active", color: "bg-emerald-100 text-emerald-800" },
  past_due: { label: "Past due", color: "bg-amber-100 text-amber-800" },
  unpaid: { label: "Unpaid", color: "bg-red-100 text-red-800" },
  canceled: { label: "Canceled", color: "bg-zinc-200 text-zinc-700" },
  incomplete: { label: "Incomplete", color: "bg-amber-100 text-amber-800" },
  incomplete_expired: { label: "Expired", color: "bg-red-100 text-red-800" },
};

export default function Billing() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/billing/me")
      .then((r) => { if (!cancelled) setOrg(r.data); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || "Could not load billing info."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const r = await api.post("/billing/portal", { return_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not open billing portal");
      setPortalLoading(false);
    }
  };

  const status = org?.subscription_status;
  const statusMeta = STATUS_LABEL[status] || { label: status || "Unknown", color: "bg-zinc-200 text-zinc-700" };
  const isInactive = status && !["active", "trialing"].includes(status);

  const formatDate = (epoch) => {
    if (!epoch) return "—";
    return new Date(epoch * 1000).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  return (
    <div className="min-h-screen bg-zinc-50">
      <header className="bg-[#1A2332] text-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <BrandMark size={32} className="rounded-md" />
            <span className="font-display font-bold">ezItemizer</span>
          </Link>
          <button
            type="button"
            onClick={async () => { await logout(); navigate("/"); }}
            data-testid="billing-logout-button"
            className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-white/70 hover:text-white"
          >
            <SignOut size={14} />
            Sign out
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 lg:px-10 py-10 lg:py-14">
        {loading && (
          <div className="bg-white border border-zinc-300 px-6 py-8 text-center text-sm text-zinc-500">
            Loading billing details…
          </div>
        )}

        {!loading && error && (
          <div className="bg-red-50 border border-red-200 p-4 text-sm text-red-700 flex items-start gap-2">
            <WarningCircle size={16} weight="fill" className="flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {!loading && !error && org && (
          <div className="space-y-6" data-testid="billing-card">
            {isInactive && (
              <div className="bg-amber-50 border border-amber-300 p-5 flex items-start gap-3">
                <WarningCircle size={20} weight="fill" className="text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-bold text-amber-900">Your subscription is inactive</div>
                  <div className="text-sm text-amber-800 mt-1">
                    Access to ezItemizer is paused until billing is restored. Contact us at <a href="mailto:support@ezitemizer.com" className="underline">support@ezitemizer.com</a> for help.
                  </div>
                </div>
              </div>
            )}

            <div className="bg-white border border-zinc-300">
              <div className="bg-[#1A2332] text-white px-6 py-4">
                <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#FF9800]">
                  Subscription
                </div>
                <div className="font-display font-bold text-xl mt-1">
                  {org.name || "Your Organization"}
                </div>
              </div>

              <div className="px-6 py-5 space-y-5">
                <Row label="Plan" value="ezItemizer Basic — $29 / month" />
                <Row
                  label="Status"
                  value={
                    <span className={`inline-block text-xs font-bold uppercase tracking-widest px-2 py-1 ${statusMeta.color}`}>
                      {statusMeta.label}
                    </span>
                  }
                />
                {org.trial_end && (
                  <Row label="Trial ends" value={formatDate(org.trial_end)} />
                )}
                {org.current_period_end && (
                  <Row label="Next renewal" value={formatDate(org.current_period_end)} />
                )}
                {org.owner_email && (
                  <Row label="Account owner" value={org.owner_email} />
                )}
                {org.is_demo && (
                  <Row
                    label="Account type"
                    value={
                      <span className="inline-block text-xs font-bold uppercase tracking-widest px-2 py-1 bg-zinc-200 text-zinc-700">
                        Demo (no billing)
                      </span>
                    }
                  />
                )}
              </div>

              <div className="px-6 py-4 border-t border-zinc-200 bg-zinc-50">
                {!org.is_demo && user?.role === "admin" && org.stripe_customer_id ? (
                  <button
                    type="button"
                    onClick={openPortal}
                    disabled={portalLoading}
                    data-testid="manage-subscription-button"
                    className="w-full flex items-center justify-center gap-2 bg-[#1A2332] hover:bg-[#243044] text-white font-bold text-xs uppercase tracking-widest px-4 py-3 disabled:opacity-60"
                  >
                    {portalLoading ? "Opening Stripe…" : (
                      <>
                        <CreditCard size={14} weight="duotone" />
                        Manage subscription
                        <ArrowSquareOut size={12} weight="bold" />
                      </>
                    )}
                  </button>
                ) : (
                  <div className="flex items-center gap-2 text-[11px] text-zinc-500">
                    <CreditCard size={14} weight="duotone" />
                    <span>
                      {org.is_demo
                        ? "Demo account — billing not applicable."
                        : user?.role === "admin"
                        ? "No Stripe customer linked yet. Subscribe to manage payment."
                        : "Only admins can manage subscription billing."}
                    </span>
                  </div>
                )}
                {!org.is_demo && user?.role === "admin" && org.stripe_customer_id && (
                  <p className="mt-2 text-center text-[10px] text-zinc-500">
                    Update your card, change billing email, or cancel — managed by Stripe.
                  </p>
                )}
              </div>
            </div>

            <div className="flex justify-between">
              <Link
                to={user?.role === "admin" && status && ["active", "trialing"].includes(status) ? "/dashboard" : "/"}
                className="text-xs font-bold uppercase tracking-widest text-zinc-500 hover:text-[#FF9800]"
              >
                {status && ["active", "trialing"].includes(status) ? "← Back to dashboard" : "← Back to homepage"}
              </Link>

              {!org.is_demo && status === "trialing" && (
                <span className="text-[11px] text-zinc-500">
                  No charge today — billed after trial.
                </span>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between border-b border-zinc-100 pb-3 last:border-0 last:pb-0">
      <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="text-sm text-[#0F172A]">{value}</div>
    </div>
  );
}
